import * as React from 'react';
import cx from 'clsx';
import { CheckIcon, SelectorIcon } from '@heroicons/react/solid';
import { Combobox as ComboboxPrimitive, Transition } from '@headlessui/react';
import { Icon, Input, InputProps, Spinner } from '@vechaiui/react';
import { useFilter } from '@react-aria/i18n';

// TODO: doesnt support multiple yet.
const ComboboxRoot = <T extends object>({
  children,
  displayValue,
  getKey,
  id,
  invalid,
  isLoading,
  items,
  onBlur,
  onChange,
  onInputChange,
  placeholder,
  readOnly,
  required,
  size,
  value,
  ...props
}: ComboboxProps<T>) => {
  const inputProps = {
    displayValue,
    id,
    invalid,
    onBlur,
    placeholder,
    readOnly,
    required,
    size,
  };
  const getItem = React.useCallback(
    (key?: string) => items.find((item) => getKey(item) === key),
    [getKey, items],
  );
  const { contains } = useFilter({ sensitivity: 'base' });
  const [selectedItem, setSelectedItem] = React.useState(getItem(value));
  const [filterText, setFilterText] = React.useState('');

  const filteredList = items.filter((item) => contains(displayValue(item), filterText));

  const handleChange = (value: T) => {
    setSelectedItem(value);
    onChange?.(getKey(value));
  };
  const handleInputChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    setFilterText(ev.target.value);
    onInputChange?.(ev);
  };

  React.useEffect(() => {
    if (!isLoading && (!selectedItem || value !== getKey(selectedItem) || items.length === 0)) {
      setSelectedItem(getItem(value));
    }
  }, [getItem, getKey, items, isLoading, selectedItem, value]);

  return (
    <ComboboxPrimitive {...props} onChange={handleChange} value={selectedItem}>
      {({ open }) => (
        <div className="relative">
          <div>
            <ComboboxPrimitive.Input {...inputProps} onChange={handleInputChange} as={Input} />
            <div className="absolute inset-y-0 flex items-center right-2">
              {!isLoading && (
                <ComboboxPrimitive.Button disabled={readOnly} className="flex">
                  <Icon
                    aria-hidden="true"
                    as={SelectorIcon}
                    className={cx('w-4 h-4', open ? 'text-primary-500' : 'text-neutral-400')}
                    label="selector"
                  />
                </ComboboxPrimitive.Button>
              )}
              {isLoading && <Spinner />}
            </div>
          </div>

          <Transition
            afterLeave={() => {
              setFilterText('');
            }}
            enter="transition ease-in-out duration-100"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition ease-out duration-75"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            show={open}
          >
            <ComboboxPrimitive.Options
              static
              className={cx(
                'absolute left-0 z-40 max-h-64 w-full mt-2 rounded-base overflow-auto shadow-sm bg-white border border-neutral-200 p-1 space-y-1',
              )}
            >
              {filteredList.map(children)}
            </ComboboxPrimitive.Options>
          </Transition>
        </div>
      )}
    </ComboboxPrimitive>
  );
};
export type ComboboxProps<T = any> = Pick<
  InputProps,
  'id' | 'invalid' | 'onBlur' | 'placeholder' | 'readOnly' | 'required' | 'size'
> & {
  children: (item: T, index: number) => React.ReactNode;
  disabled?: boolean;
  displayValue: (item: T) => string;
  getKey: (item: T) => string;
  isLoading?: boolean;
  items: T[];
  name?: string;
  nullable?: boolean;
  onChange?: (item: string) => void;
  onInputChange?: React.ChangeEventHandler<HTMLInputElement>;
  value?: string;
};

const ComboboxOption = <T extends object>({ children, ...props }: ComboboxOptionProps<T>) => {
  return (
    <ComboboxPrimitive.Option className="relative" {...props}>
      {({ active, selected, disabled }) => (
        <button
          aria-disabled={disabled}
          className={cx(
            'flex items-center w-full px-4 pl-10 py-2 text-left cursor-base rounded-base',
            active && 'bg-neutral-100',
            selected && 'bg-primary-50 text-primary-800',
          )}
          disabled={disabled}
          type="button"
        >
          <span className={cx('flex-1 block', selected && 'font-bold')}>{children}</span>
          {selected && (
            <span className="absolute inset-y-0 left-0 flex items-center justify-center w-10">
              <Icon as={CheckIcon} label="check" className="w-5 h-5 text-primary-500" />
            </span>
          )}
        </button>
      )}
    </ComboboxPrimitive.Option>
  );
};
export type ComboboxOptionProps<T = any> = {
  children?: React.ReactNode;
  disabled?: boolean;
  value: T;
};

export default Object.assign(ComboboxRoot, { Option: ComboboxOption });
